import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Upload, Music, X, Download, Loader } from "lucide-react";
import {
    addWatermarkToAudio,
    downloadWatermarkedFile,
    downloadAllWatermarkedFiles,
    resetWatermarkState,
    resetDownloadState,
} from "../../redux/reduxSlices/audioUploadSlice";

export const WatermarkUploadModal = ({ isOpen, onClose }) => {
    const dispatch = useDispatch();

    const [showResults, setShowResults] = useState(false);
    const [watermarkResponse, setWatermarkResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Safely get the upload state with fallbacks
    const uploadState = useSelector((state) => state?.upload || {});
    const { error = null, downloadLoading = false, downloadError = null, downloadAllLoading = false } = uploadState;

    const [files, setFiles] = useState([]);
    const [volume, setVolume] = useState(-5);
    const [downloadingFile, setDownloadingFile] = useState(null);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);

        if (selectedFiles.length === 0) return;

        // ✅ Append newly selected files (instead of replacing)
        setFiles((prev) => {
            const existingKeys = new Set(prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
            const deduped = selectedFiles.filter(
                (f) => !existingKeys.has(`${f.name}-${f.size}-${f.lastModified}`)
            );
            return [...prev, ...deduped];
        });

        // allow selecting the same file again after removing
        e.target.value = "";

        if (error) dispatch(resetWatermarkState());
    };

    const handleRemoveFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleClearAll = () => {
        setFiles([]);
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        if (files.length === 0) {
            setIsLoading(false);
            return;
        }

        try {
            const payload = { files, volume };
            const response = await dispatch(addWatermarkToAudio(payload)).unwrap();

            if (response?.success) {
                setWatermarkResponse(response);
                setShowResults(true);
            }
        } catch (err) {
            console.log("error", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = (filename) => {
        if (!filename) return;
        setDownloadingFile(filename);
        dispatch(downloadWatermarkedFile(filename));
    };

    const handleDownloadAll = () => {
        if (!watermarkResponse?.files || watermarkResponse.files.length === 0) return;

        // Get all watermarked filenames
        const filenames = watermarkResponse.files
            .map(f => f?.watermarked_filename)
            .filter(Boolean);

        if (filenames.length > 0) {
            dispatch(downloadAllWatermarkedFiles(filenames));
        }
    };

    const handleClose = () => {
        setFiles([]);
        setVolume(-5);
        setDownloadingFile(null);
        setWatermarkResponse(null);
        setShowResults(false);
        setIsLoading(false);
        dispatch(resetWatermarkState());
        dispatch(resetDownloadState());
        onClose();
    };

    const handleProcessMore = () => {
        setFiles([]);
        setWatermarkResponse(null);
        setShowResults(false);
        setDownloadingFile(null);
        dispatch(resetWatermarkState());
        dispatch(resetDownloadState());
    };

    // Reset download error after 5 seconds
    useEffect(() => {
        if (downloadError) {
            const timer = setTimeout(() => {
                dispatch(resetDownloadState());
                setDownloadingFile(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [downloadError, dispatch]);

    if (!isOpen) return null;

    const resultFiles = watermarkResponse?.files || [];
    const hasMultipleFiles = resultFiles.length > 1;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-2">
            <div
                className="bg-white rounded-lg border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
                    <div className="flex items-center gap-2">
                        <Music className="w-5 h-5 text-blue-600" />
                        <h2 className="text-base font-bold text-gray-900">Audio Watermarking</h2>

                        {files.length > 0 && !showResults && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {files.length} file{files.length > 1 ? "s" : ""} selected
                            </span>
                        )}
                    </div>

                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4">
                    {!showResults ? (
                        <form onSubmit={handleUpload} className="space-y-4">
                            {/* File Upload Area */}
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                                <input
                                    type="file"
                                    id="audio-upload"
                                    accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    multiple // ✅ allow multiple
                                />
                                <label htmlFor="audio-upload" className="cursor-pointer block">
                                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                    <p className="text-gray-700 font-medium mb-1">Upload Audio Files</p>
                                    <p className="text-gray-500 text-sm">Click to browse audio files</p>
                                    <p className="text-gray-400 text-xs mt-2">You can upload multiple files at once</p>
                                </label>

                                {files.length > 0 && (
                                    <div className="mt-4 space-y-2 text-left">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-medium text-gray-700">
                                                Selected files ({files.length})
                                            </p>
                                            <button
                                                type="button"
                                                onClick={handleClearAll}
                                                className="text-xs text-red-600 hover:text-red-700"
                                                disabled={isLoading}
                                            >
                                                Clear all
                                            </button>
                                        </div>

                                        <div className="space-y-2 max-h-56 overflow-auto pr-1">
                                            {files.map((file, idx) => (
                                                <div
                                                    key={`${file.name}-${file.size}-${file.lastModified}-${idx}`}
                                                    className="flex items-center justify-between bg-gray-50 p-3 rounded border border-gray-200"
                                                >
                                                    <div className="flex items-center gap-2 flex-1">
                                                        <Music className="w-4 h-4 text-gray-500" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-700 truncate">
                                                                {file.name}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {(file.size / (1024 * 1024)).toFixed(2)} MB
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveFile(idx)}
                                                        className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                                                        title="Remove file"
                                                        disabled={isLoading}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Volume Control */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Watermark Volume: {volume} dB
                                </label>
                                <div className="flex items-center gap-4">
                                    <span className="text-xs text-gray-500">-30 dB</span>
                                    <input
                                        type="range"
                                        min="-30"
                                        max="20"
                                        value={volume}
                                        onChange={(e) => setVolume(parseInt(e.target.value))}
                                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                    />
                                    <span className="text-xs text-gray-500">+20 dB</span>
                                </div>
                                <div className="flex justify-between text-xs text-gray-500">
                                    <span>Quieter</span>
                                    <span>Louder</span>
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                                    {error}
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={isLoading || files.length === 0}
                                    className="px-4 py-2 rounded text-white text-sm font-medium bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 flex items-center gap-1.5 disabled:opacity-50"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader className="w-3.5 h-3.5 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <Music className="w-3.5 h-3.5" />
                                            Add Watermark
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    ) : (
                        /* Results Display */
                        <div className="space-y-4">
                            {/* Success Message */}
                            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                                <div className="flex items-center gap-2">
                                    <Music className="w-5 h-5" />
                                    <div>
                                        <span className="font-medium">
                                            {watermarkResponse?.message ||
                                                `Watermarking completed for ${resultFiles.length} file${resultFiles.length > 1 ? "s" : ""
                                                }!`}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Download All Button (only shown when multiple files) */}
                            {hasMultipleFiles && (
                                <div className="flex justify-end">
                                    <button
                                        onClick={handleDownloadAll}
                                        disabled={downloadAllLoading || downloadLoading || resultFiles.length === 0}
                                        className="px-4 py-2 rounded text-white text-sm font-medium bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {downloadAllLoading ? (
                                            <>
                                                <Loader className="w-4 h-4 animate-spin" />
                                                Downloading All...
                                            </>
                                        ) : (
                                            <>
                                                <Download className="w-4 h-4" />
                                                Download All ({resultFiles.length} files)
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}

                            {/* Download list */}
                            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                                {resultFiles.length === 0 ? (
                                    <div className="text-sm text-gray-600">No output files found.</div>
                                ) : (
                                    <div className="space-y-2">
                                        {resultFiles.map((f, idx) => {
                                            const outName = f?.watermarked_filename;
                                            const label = f?.original_filename || outName || `File ${idx + 1}`;
                                            const isThisDownloading = downloadLoading && downloadingFile === outName;

                                            return (
                                                <div
                                                    key={`${outName || idx}`}
                                                    className="flex items-center justify-between bg-gray-50 p-3 rounded border border-gray-200"
                                                >
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                        <Music className="w-4 h-4 text-gray-500" />
                                                        <p className="text-sm font-medium text-gray-700 truncate">{label}</p>
                                                    </div>

                                                    <button
                                                        onClick={() => handleDownload(outName)}
                                                        disabled={downloadLoading || downloadAllLoading || !outName}
                                                        className="px-3 py-2 rounded text-white text-xs font-medium bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 flex items-center gap-2 disabled:opacity-50"
                                                    >
                                                        {isThisDownloading ? (
                                                            <>
                                                                <Loader className="w-4 h-4 animate-spin" />
                                                                Downloading...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Download className="w-4 h-4" />
                                                                Download
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Download Error */}
                            {downloadError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                                    <p className="font-medium">Download Failed</p>
                                    <p className="text-sm">{downloadError}</p>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200">
                                <button
                                    onClick={handleProcessMore}
                                    className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                                >
                                    Process More
                                </button>
                                <button
                                    onClick={handleClose}
                                    className="px-4 py-2 rounded text-white text-sm font-medium bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WatermarkUploadModal;